import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import type { PayslipPerson } from './PayslipUploadSheet';

export interface PayslipGroup {
  id: string;
  file: File;
  sourceFileName: string;
  pages: number[];
  matchType: 'cf' | 'name' | 'unmatched';
  detectedHint?: string;
  userId: number | null;
}

interface PayslipMatchStepProps {
  files: File[];
  people: PayslipPerson[];
  groups: PayslipGroup[];
  onGroupsChange: (groups: PayslipGroup[]) => void;
  onBack: () => void;
  onNext: () => void;
}

const NONE = '__none__';

/** Step 2: split every PDF by employee (codice fiscale, then name) and let the operator fix the assignment. */
export const PayslipMatchStep: React.FC<PayslipMatchStepProps> = ({ files, people, groups, onGroupsChange, onBack, onNext }) => {
  const { t } = useTranslation();
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzedFor, setAnalyzedFor] = useState<File[] | null>(null);

  useEffect(() => {
    if (analyzedFor === files) return;
    let cancelled = false;
    (async () => {
      setAnalyzing(true);
      const result: PayslipGroup[] = [];
      try {
        const { splitPayslipPdfByUser } = await import('../../../lib/payslipPdfSplitter');
        for (const file of files) {
          const split = await splitPayslipPdfByUser({
            file,
            users: people.map((p) => ({ id: p.id, name: p.name, codiceFiscale: p.codiceFiscale })),
          });
          split.matched.forEach((group, index) => {
            result.push({
              id: `${file.name}-${index}`,
              file: group.file,
              sourceFileName: file.name,
              pages: group.pages,
              matchType: group.matchType,
              detectedHint: group.detectedFiscalCode || group.detectedRawHint,
              userId: group.matchType === 'unmatched' || !group.userId ? null : group.userId,
            });
          });
        }
      } catch (error) {
        console.error('Payslip split failed:', error);
        files.forEach((file, index) => result.push({ id: `${file.name}-${index}`, file, sourceFileName: file.name, pages: [], matchType: 'unmatched', userId: null }));
      }
      if (!cancelled) {
        onGroupsChange(result);
        setAnalyzedFor(files);
        setAnalyzing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files, people, analyzedFor, onGroupsChange]);

  const counts = {
    cf: groups.filter((g) => g.matchType === 'cf').length,
    name: groups.filter((g) => g.matchType === 'name').length,
    unmatched: groups.filter((g) => g.userId === null).length,
  };
  const assigned = groups.filter((g) => g.userId !== null).length;
  const personById = (id: number | null) => people.find((p) => p.id === id);

  if (analyzing) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('documents.payslipUpload.match.analyzing', 'Analisi dei PDF in corso...')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="secondary">{t('documents.payslipUpload.match.matchedByCf', '{{count}} per codice fiscale', { count: counts.cf })}</Badge>
        <Badge variant="secondary">{t('documents.payslipUpload.match.matchedByName', '{{count}} per nome', { count: counts.name })}</Badge>
        <Badge variant={counts.unmatched > 0 ? 'destructive' : 'secondary'}>
          {t('documents.payslipUpload.match.unmatched', '{{count}} da assegnare', { count: counts.unmatched })}
        </Badge>
        {(files.length > 1 || groups.length > 10) && (
          <span className="text-muted-foreground">{t('documents.payslipUpload.match.desktopHint', 'Consigliato da desktop')}</span>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('documents.payslipUpload.match.noGroups', 'Nessuna busta paga rilevata nei file selezionati.')}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {groups.map((group) => {
            const person = personById(group.userId);
            return (
              <li key={group.id} className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-[1fr_16rem] sm:items-center">
                <div className="min-w-0 text-sm">
                  <p className="truncate font-medium">
                    {group.sourceFileName}
                    {group.pages.length > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        · {t('documents.payslipUpload.match.pages', 'pagine {{pages}}', { pages: group.pages.join(', ') })}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {group.detectedHint
                      ? t('documents.payslipUpload.match.detected', 'Rilevato: {{hint}}', { hint: group.detectedHint })
                      : t('documents.payslipUpload.match.notDetected', 'Nessun dipendente rilevato')}
                    {person?.isActive === false && ` · ${t('documents.payslipUpload.match.inactive', 'cessato · nessuna notifica')}`}
                  </p>
                </div>
                <Select
                  value={group.userId === null ? NONE : String(group.userId)}
                  onValueChange={(value) => onGroupsChange(groups.map((g) => (g.id === group.id ? { ...g, userId: value === NONE ? null : Number(value) } : g)))}
                >
                  <SelectTrigger className={group.userId === null ? 'border-destructive' : undefined}>
                    <SelectValue placeholder={t('documents.payslipUpload.match.choose', 'Scegli il dipendente')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('documents.payslipUpload.match.skip', 'Salta questo gruppo')}</SelectItem>
                    {people.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                        {p.isActive === false ? ` (${t('documents.filters.inactive', 'cessato')})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <Button variant="outline" onClick={onBack}>
          {t('documents.payslipUpload.match.back', 'Indietro')}
        </Button>
        <Button className="bg-orange-500 hover:bg-orange-600" disabled={assigned === 0} onClick={onNext}>
          {t('documents.payslipUpload.match.next', 'Carica {{count}} buste paga', { count: assigned })}
        </Button>
      </div>
    </div>
  );
};
